export interface VideoSyncRequest {
  source: string;
  targetTimeSeconds: number;
  shouldPlay: boolean;
  allowPlaybackFastPath?: boolean;
  clockIdentity: {
    raceTimeMs: number;
    carId: string;
    seekGeneration: number;
  };
}

export interface VideoSyncAcknowledgement {
  accepted: boolean;
  original_sender_id: string;
  race_time_ms: number;
  play_state: "playing" | "paused";
  car_id: string;
  seek_generation: number;
}

// Keep an internal margin below the frozen external 100 ms limit.
export const FRAME_TIME_TOLERANCE_SECONDS = 0.05;
const HAVE_METADATA = 1;
const HAVE_FUTURE_DATA = 3;
const INITIAL_SYNC_PLAYBACK_RATE = 0.25;
const PAUSED_HANDOFF_PLAYBACK_RATE = 0.125;
export const TARGET_FRAME_TIMEOUT_MS = 30_000;

export interface VideoSynchronizerOptions {
  pausedHandoffMinimumMs?: number;
  pausedHandoffMaximumMs?: number;
  targetFrameTimeoutMs?: number;
  sourceMatches?: (video: HTMLVideoElement, source: string) => boolean;
  loadSource?: (video: HTMLVideoElement, source: string) => void | Promise<void>;
}

export async function publishWhileVideoSynchronizes<T>(
  startVideoSynchronization: () => Promise<boolean>,
  publishSharedState: () => void,
  applyDisplayedState: () => void,
): Promise<boolean> {
  // Start media work in the user event turn, but do not expose the new clock
  // generation until the target frame is real. This also covers a seek to the
  // already-displayed race time, where text equality cannot signal readiness.
  const synchronized = await startVideoSynchronization();
  if (synchronized) {
    applyDisplayedState();
    publishSharedState();
  }
  return synchronized;
}

class VideoSyncCancelled extends Error {
  constructor() {
    super("video synchronization was superseded");
    this.name = "VideoSyncCancelled";
  }
}

function absoluteUrl(value: string): string {
  const base = globalThis.document?.baseURI ?? "http://127.0.0.1/";
  return new URL(value, base).href;
}

function sameSource(video: HTMLVideoElement, source: string): boolean {
  const current = video.currentSrc || video.src;
  return Boolean(current) && absoluteUrl(current) === absoluteUrl(source);
}

export function canUseVideoPlaybackFastPath(
  video: HTMLVideoElement,
  source: string,
  targetTimeSeconds: number,
): boolean {
  return sameSource(video, source)
    && !video.seeking
    && video.readyState >= HAVE_FUTURE_DATA
    && Math.abs(video.currentTime - targetTimeSeconds) <= FRAME_TIME_TOLERANCE_SECONDS;
}

function assertCurrent(signal: AbortSignal): void {
  if (signal.aborted) throw new VideoSyncCancelled();
}

function waitForCondition(
  video: HTMLVideoElement,
  signal: AbortSignal,
  events: string[],
  condition: () => boolean,
): Promise<void> {
  assertCurrent(signal);
  if (condition()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      for (const event of events) video.removeEventListener(event, check);
      video.removeEventListener("error", fail);
      signal.removeEventListener("abort", cancel);
    };
    const finish = () => {
      cleanup();
      resolve();
    };
    const check = () => {
      if (condition()) finish();
    };
    const fail = () => {
      cleanup();
      reject(new Error(`video media error while synchronizing ${video.currentSrc || video.src}`));
    };
    const cancel = () => {
      cleanup();
      reject(new VideoSyncCancelled());
    };
    for (const event of events) video.addEventListener(event, check);
    video.addEventListener("error", fail, { once: true });
    signal.addEventListener("abort", cancel, { once: true });
    check();
  });
}

function waitForTargetFrame(
  video: HTMLVideoElement,
  signal: AbortSignal,
  targetTimeSeconds: number,
  timeoutMs: number,
): Promise<void> {
  assertCurrent(signal);
  if (typeof video.requestVideoFrameCallback !== "function") {
    throw new Error("requestVideoFrameCallback is required for fixed-point video synchronization");
  }
  return new Promise((resolve, reject) => {
    let callbackId = 0;
    let settled = false;
    const timeoutId = globalThis.setTimeout(() => {
      if (settled) return;
      settled = true;
      if (typeof video.cancelVideoFrameCallback === "function") video.cancelVideoFrameCallback(callbackId);
      signal.removeEventListener("abort", cancel);
      reject(new Error(`video target frame timed out at ${targetTimeSeconds}`));
    }, timeoutMs);
    const cleanup = () => {
      globalThis.clearTimeout(timeoutId);
      signal.removeEventListener("abort", cancel);
    };
    const cancel = () => {
      if (settled) return;
      settled = true;
      if (typeof video.cancelVideoFrameCallback === "function") video.cancelVideoFrameCallback(callbackId);
      cleanup();
      reject(new VideoSyncCancelled());
    };
    const observe = (_now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata) => {
      if (settled) return;
      if (signal.aborted) {
        cancel();
        return;
      }
      if (Math.abs(metadata.mediaTime - targetTimeSeconds) <= FRAME_TIME_TOLERANCE_SECONDS) {
        settled = true;
        cleanup();
        resolve();
        return;
      }
      if (metadata.mediaTime > targetTimeSeconds + FRAME_TIME_TOLERANCE_SECONDS) {
        settled = true;
        cleanup();
        reject(new Error(`video target frame was passed at ${metadata.mediaTime}`));
        return;
      }
      callbackId = video.requestVideoFrameCallback(observe);
    };
    signal.addEventListener("abort", cancel, { once: true });
    callbackId = video.requestVideoFrameCallback(observe);
  });
}

export class VideoSynchronizer {
  private readonly pausedHandoffMinimumMs: number;
  private readonly pausedHandoffMaximumMs: number;
  private readonly targetFrameTimeoutMs: number;
  private readonly sourceMatches: (video: HTMLVideoElement, source: string) => boolean;
  private readonly loadSource: (video: HTMLVideoElement, source: string) => void | Promise<void>;

  constructor(options: VideoSynchronizerOptions = {}) {
    this.pausedHandoffMinimumMs = options.pausedHandoffMinimumMs ?? 180;
    this.pausedHandoffMaximumMs = options.pausedHandoffMaximumMs ?? 700;
    this.targetFrameTimeoutMs = options.targetFrameTimeoutMs ?? TARGET_FRAME_TIMEOUT_MS;
    this.sourceMatches = options.sourceMatches ?? sameSource;
    this.loadSource = options.loadSource ?? ((video, source) => {
      video.src = source;
      video.load();
    });
  }

  private active: {
    controller: AbortController;
    video: HTMLVideoElement;
    request: VideoSyncRequest;
    pausedHandoffReady: boolean;
    handoffArmed: boolean;
    acknowledged: boolean;
    pauseCallbackId: number | null;
    pauseScheduleRevision: number;
    handoffTimeoutId: number | null;
    handoffStartedAt: number | null;
    handoffCompletion: Promise<boolean>;
    finishHandoff: (completed: boolean) => void;
    handoffSettled: boolean;
  } | null = null;

  private settleHandoff(
    active: NonNullable<VideoSynchronizer["active"]>,
    completed: boolean,
  ): void {
    if (active.handoffSettled) return;
    active.handoffSettled = true;
    active.finishHandoff(completed);
  }

  private completePausedHandoff(active: NonNullable<VideoSynchronizer["active"]>): void {
    if (this.active?.controller !== active.controller || active.controller.signal.aborted) return;
    if (active.pauseCallbackId !== null && typeof active.video.cancelVideoFrameCallback === "function") {
      active.video.cancelVideoFrameCallback(active.pauseCallbackId);
    }
    if (active.handoffTimeoutId !== null) globalThis.clearTimeout(active.handoffTimeoutId);
    active.pauseCallbackId = null;
    active.handoffTimeoutId = null;
    active.video.pause();
    active.video.playbackRate = 1;
    this.active = null;
    this.settleHandoff(active, true);
  }

  cancel(): void {
    const active = this.active;
    this.active = null;
    if (active) {
      if (active.pauseCallbackId !== null && typeof active.video.cancelVideoFrameCallback === "function") {
        active.video.cancelVideoFrameCallback(active.pauseCallbackId);
      }
      if (active.handoffTimeoutId !== null) globalThis.clearTimeout(active.handoffTimeoutId);
      active.controller.abort();
      active.video.playbackRate = 1;
      active.video.pause();
      this.settleHandoff(active, false);
    }
  }

  private schedulePauseAfterFrames(
    active: NonNullable<VideoSynchronizer["active"]>,
    frameCount: number,
  ): void {
    if (active.pauseCallbackId !== null && typeof active.video.cancelVideoFrameCallback === "function") {
      active.video.cancelVideoFrameCallback(active.pauseCallbackId);
    }
    if (active.handoffTimeoutId !== null) globalThis.clearTimeout(active.handoffTimeoutId);
    const scheduleRevision = ++active.pauseScheduleRevision;
    const elapsed = performance.now() - (active.handoffStartedAt ?? performance.now());
    active.handoffTimeoutId = globalThis.setTimeout(() => {
      if (active.pauseScheduleRevision !== scheduleRevision) return;
      this.completePausedHandoff(active);
    }, Math.max(0, this.pausedHandoffMaximumMs - elapsed));
    let remaining = frameCount;
    const observe = () => {
      if (this.active?.controller !== active.controller || active.controller.signal.aborted
        || active.pauseScheduleRevision !== scheduleRevision) return;
      remaining -= 1;
      if (remaining > 0) {
        active.pauseCallbackId = active.video.requestVideoFrameCallback(observe);
        return;
      }
      active.pauseCallbackId = null;
      // All callbacks for one decoded frame run in one task. The microtask
      // pauses after an external observer has read that same real frame.
      queueMicrotask(() => {
        if (this.active?.controller !== active.controller || active.controller.signal.aborted
          || active.pauseScheduleRevision !== scheduleRevision) return;
        const elapsed = performance.now() - (active.handoffStartedAt ?? performance.now());
        const remainingDelay = Math.max(0, this.pausedHandoffMinimumMs - elapsed);
        if (remainingDelay > 0) {
          if (active.handoffTimeoutId !== null) globalThis.clearTimeout(active.handoffTimeoutId);
          active.handoffTimeoutId = globalThis.setTimeout(
            () => {
              if (active.pauseScheduleRevision !== scheduleRevision) return;
              this.completePausedHandoff(active);
            },
            remainingDelay,
          );
          return;
        }
        this.completePausedHandoff(active);
      });
    };
    active.pauseCallbackId = active.video.requestVideoFrameCallback(observe);
  }

  armDisplayedHandoffPause(video: HTMLVideoElement): boolean {
    const active = this.active;
    // A play-state-only fast path has already paused the exact displayed
    // frame, so there is no decoding handoff left to arm.
    if (!active) return video.paused;
    if (active.video !== video || !active.pausedHandoffReady) return false;
    if (active.handoffArmed) return true;
    active.handoffArmed = true;
    active.handoffStartedAt = performance.now();
    // At one-eighth speed, one callback leaves observers enough wall time to
    // register while advancing by only one decoded source frame.
    this.schedulePauseAfterFrames(active, 1);
    return true;
  }

  hasExternallyObservedPausedHandoff(video: HTMLVideoElement): boolean {
    const active = this.active;
    return Boolean(active
      && active.video === video
      && active.pausedHandoffReady
      && active.handoffArmed
      && active.acknowledged
      && !active.request.shouldPlay);
  }

  hasPendingPausedHandoff(video: HTMLVideoElement): boolean {
    const active = this.active;
    return Boolean(active && active.video === video && active.pausedHandoffReady
      && active.handoffArmed && !active.request.shouldPlay && !active.controller.signal.aborted);
  }

  waitForPausedHandoff(video: HTMLVideoElement): Promise<boolean> {
    const active = this.active;
    if (!active || active.video !== video || !active.pausedHandoffReady || !active.handoffArmed) {
      return Promise.resolve(video.paused);
    }
    return active.handoffCompletion;
  }

  acceptClockAcknowledgement(acknowledgement: VideoSyncAcknowledgement): boolean {
    const active = this.active;
    if (!active || !active.pausedHandoffReady || active.request.shouldPlay) return false;
    const identity = active.request.clockIdentity;
    const matches = acknowledgement.accepted
      && acknowledgement.original_sender_id === "fixed-point-race-screen"
      && acknowledgement.play_state === "paused"
      && acknowledgement.race_time_ms === identity.raceTimeMs
      && acknowledgement.car_id === identity.carId
      && acknowledgement.seek_generation === identity.seekGeneration;
    if (!matches) return false;
    active.acknowledged = true;
    if (active.handoffArmed) this.schedulePauseAfterFrames(active, 1);
    return true;
  }

  async synchronize(video: HTMLVideoElement, request: VideoSyncRequest): Promise<boolean> {
    const fastPath = request.allowPlaybackFastPath === true
      && this.sourceMatches(video, request.source)
      && !video.seeking
      && video.readyState >= HAVE_FUTURE_DATA
      && Math.abs(video.currentTime - request.targetTimeSeconds) <= FRAME_TIME_TOLERANCE_SECONDS;
    this.cancel();
    if (fastPath) {
      video.playbackRate = 1;
      if (request.shouldPlay) await video.play();
      else video.pause();
      return true;
    }
    const controller = new AbortController();
    let finishHandoff!: (completed: boolean) => void;
    const handoffCompletion = new Promise<boolean>(resolve => { finishHandoff = resolve; });
    this.active = {
      controller,
      video,
      request,
      pausedHandoffReady: false,
      handoffArmed: false,
      acknowledged: false,
      pauseCallbackId: null,
      pauseScheduleRevision: 0,
      handoffTimeoutId: null,
      handoffStartedAt: null,
      handoffCompletion,
      finishHandoff,
      handoffSettled: false,
    };
    const { signal } = controller;
    let targetFrameReady: Promise<void> | null = null;
    try {
      video.pause();
      video.playbackRate = 1;

      if (!this.sourceMatches(video, request.source)) {
        const metadataReady = waitForCondition(
          video,
          signal,
          ["loadedmetadata", "durationchange"],
          () => this.sourceMatches(video, request.source) && video.readyState >= HAVE_METADATA,
        );
        await this.loadSource(video, request.source);
        await metadataReady;
      } else {
        await waitForCondition(
          video,
          signal,
          ["loadedmetadata", "durationchange"],
          () => video.readyState >= HAVE_METADATA,
        );
      }
      assertCurrent(signal);

      const duration = video.duration;
      const target = Number.isFinite(duration) && duration > 0
        ? Math.min(Math.max(request.targetTimeSeconds, 0), duration)
        : Math.max(request.targetTimeSeconds, 0);
      const seekComplete = waitForCondition(
        video,
        signal,
        ["seeked"],
        () => !video.seeking && Math.abs(video.currentTime - target) <= FRAME_TIME_TOLERANCE_SECONDS,
      );
      video.currentTime = target;
      // Register immediately after issuing the seek. Chrome may present the
      // requested paused frame before the seeked/canplay promises settle.
      targetFrameReady = waitForTargetFrame(
        video, signal, target, this.targetFrameTimeoutMs,
      );
      // The seek itself may present or reject the frame before the remaining
      // readiness checks finish. Mark that early outcome as handled; the
      // awaited promise below still carries the original result.
      void targetFrameReady.catch(() => undefined);
      await seekComplete;
      await waitForCondition(
        video,
        signal,
        ["loadeddata", "canplay", "canplaythrough", "progress", "seeked"],
        () => !video.seeking && video.readyState >= HAVE_FUTURE_DATA,
      );
      // A paused seek can fall back to HAVE_METADATA in Chrome after its one
      // decoded frame is presented. Muted play primes real future frames; the
      // first matching decoded frame is then the boundary for pausing.
      // Register before playback starts. If registration happens after
      // play(), Chrome can present and pass the requested frame first; every
      // later callback is then beyond the fixed tolerance and can never
      // satisfy this request.
      video.playbackRate = INITIAL_SYNC_PLAYBACK_RATE;
      try {
        await video.play();
      } catch (error) {
        controller.abort();
        await targetFrameReady.catch(() => undefined);
        throw error;
      }
      assertCurrent(signal);
      await targetFrameReady;
      assertCurrent(signal);

      if (request.shouldPlay) {
        const active = this.active;
        if (active?.controller === controller) {
          video.playbackRate = 1;
          this.active = null;
          this.settleHandoff(active, true);
        }
      } else {
        const active = this.active;
        if (active?.controller === controller) {
          // The shared paused clock is published immediately after this real
          // frame. Keep a short observation handoff without racing several
          // full-speed frames past the frozen target before the observer can
          // register its callback.
          video.playbackRate = PAUSED_HANDOFF_PLAYBACK_RATE;
          active.pausedHandoffReady = true;
        }
      }
      return true;
    } catch (error) {
      const wasCancelled = error instanceof VideoSyncCancelled || signal.aborted;
      controller.abort();
      await targetFrameReady?.catch(() => undefined);
      if (wasCancelled) {
        const active = this.active;
        if (active?.controller === controller) {
          video.playbackRate = 1;
          video.pause();
          this.active = null;
          this.settleHandoff(active, false);
        }
        return false;
      }
      if (this.active?.controller === controller) {
        const active = this.active;
        video.playbackRate = 1;
        video.pause();
        this.active = null;
        this.settleHandoff(active, false);
      }
      throw error;
    }
  }
}
